import { memo } from "react"
import type { FC, NamedExoticComponent, ReactElement } from "react"

import { AnimatePresence, motion } from "framer-motion"
import isEmpty from "lodash-es/isEmpty"
import map from "lodash-es/map"
import { NavLink } from "react-router-dom"

import { ArrowIcon } from "@/3514/assets"
import { THEME } from "@/3514/theme"
import { ERoundDirection, propsAreEqual } from "@/3514/utils"

import classes from "./coach.breadcrumbs.styles"
import type { TCoachBreadcrumb as TItem } from "./coach.breadcrumbs.types"

type TProps = { isVisible: boolean; isReady: boolean; items: TItem[] }

/**
 * Coach' side navigation breadcrumbs.
 * Design: {@link https://www.figma.com/design/QLhX4KcIViCdMD12xdfvXq/Coach?node-id=5194-74594&m=dev}
 *
 * @param isVisible bool prop
 * @param isReady bool prop
 * @param items sessions navigation items
 * @return ReactElement
 */
const Breadcrumbs: FC<TProps> = ({ isVisible, isReady, items }: TProps): ReactElement => (
    <AnimatePresence mode="wait" initial={false}>
        {isEmpty(items) || !isVisible ? null : (
            <div key={`c-brs-${items.length}`} className={classes.container({ isReady })}>
                <div className={classes.wrapper()}>
                    {map<TItem, ReactElement>(
                        items,
                        (b: TItem, idx: number): ReactElement => (
                            <motion.div
                                key={`c-b-${b.label}`}
                                className={classes.item()}
                                onMouseOver={b.onHover}
                                onFocus={b.onHover}
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.25, delay: idx * 0.06, ease: "easeInOut" }}
                            >
                                {idx === 0 ? null : (
                                    <ArrowIcon
                                        direction={ERoundDirection.Right}
                                        fillColor={THEME.COLORS.gray[300]}
                                        className={classes.arrow()}
                                    />
                                )}

                                <NavLink
                                    to={b.url}
                                    className={classes.itemLabel({ isActive: b.isCurrent, variant: b.variant })}
                                >
                                    {b.label}
                                </NavLink>
                            </motion.div>
                        )
                    )}
                </div>
            </div>
        )}
    </AnimatePresence>
)

Breadcrumbs.displayName = "CoachBreadcrumbs"

const MemoizedBreadcrumbs: NamedExoticComponent<TProps> = memo(Breadcrumbs, propsAreEqual)

export { MemoizedBreadcrumbs as CoachBreadcrumbs }
